import { useUpdatePassword } from "@babackapp/private-api-client/client/use-update-password";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import zxcvbn from "zxcvbn";
import Loading from "@/components-legacy/loading.tsx";

const formSchema = z
	.object({
		oldPassword: z
			.string()
			.min(8, "Le mot de passe actuel doit contenir au moins 8 caractères"),
		newPassword: z
			.string()
			.min(8, "Le nouveau mot de passe doit contenir au moins 8 caractères")
			.refine(
				(password) => {
					const { score } = zxcvbn(password);
					return score >= 3;
				},
				{
					message:
						"Le mot de passe est trop faible. Essayez d'utiliser des majuscules, des chiffres ou des symboles.",
				},
			),
		confirmPassword: z
			.string()
			.min(
				8,
				"La confirmation du mot de passe doit contenir au moins 8 caractères",
			),
	})
	.refine((data) => data.newPassword !== data.oldPassword, {
		message:
			"Le nouveau mot de passe doit être différent du mot de passe actuel",
		path: ["newPassword"],
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message:
			"La confirmation du mot de passe ne correspond pas au nouveau mot de passe",
		path: ["confirmPassword"],
	});

export default function EditPasswordForm() {
	const t = useTranslation();
	const updatePassword = useUpdatePassword();
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			confirmPassword: "",
			oldPassword: "",
			newPassword: "",
		},
	});

	const onSubmit = form.handleSubmit((data) => {
		updatePassword.mutate({
			body: {
				oldPassword: data.oldPassword,
				newPassword: data.newPassword,
			},
		});

		form.reset();
	});

	return (
		<Form {...form}>
			<form onSubmit={onSubmit} className="flex flex-col gap-4">
				<FormField
					control={form.control}
					name={"oldPassword"}
					render={({ field }) => (
						<FormInput
							type="password"
							label={t("accountSettings.currentPassword")}
							{...field}
						/>
					)}
				/>
				<FormField
					control={form.control}
					name={"newPassword"}
					render={({ field }) => (
						<FormInput
							type="password"
							label={t("accountSettings.newPassword")}
							{...field}
						/>
					)}
				/>
				<FormField
					control={form.control}
					name={"confirmPassword"}
					render={({ field }) => (
						<FormInput
							type="password"
							label={t("accountSettings.newPassword1")}
							{...field}
						/>
					)}
				/>
				<Button type={"submit"} disabled={updatePassword.isPending}>
					{updatePassword.isPending ? (
						<Loading className="w-4 h-4" />
					) : (
						t("misc.save")
					)}
				</Button>
			</form>
		</Form>
	);
}
